// src/auth/AuthContext.tsx
import React, {
  createContext,
  useState,
  useEffect,
  useContext
} from 'react';
import api from '../api/axios';

export interface User {
  id: string;
  email: string;
  username: string;
  // …more fields as needed
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithWallet: (pubkey: string) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>(null!);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string|null>(localStorage.getItem('token'));
  const [user, setUser] = useState<User|null>(null);

  // Whenever token changes, write it and fetch the user profile
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // fetch /auth/me
      api.get<{ user: User }>('/auth/me')
        .then(res => setUser(res.data.user))
        .catch(() => {
          setUser(null);
          setToken(null);
        });
    } else {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
    }
  }, [token]);

  const login = async (email: string, password: string) => {
    const { data } = await api.post<{ token: string }>('/auth/login', { email, password });
    setToken(data.token);
  };

  const loginWithWallet = async (pubkey: string) => {
    const { data } = await api.post<{ token: string }>('/auth/sol', { publicKey: pubkey });
    setToken(data.token);
  };

  const logout = () => {
    setToken(null);
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!user) throw new Error("No user to update");
    const { data } = await api.put<{ user: User }>('/auth/me', updates);
    setUser(data.user);
  };

  return (
    <AuthContext.Provider value={{ token, user, login, loginWithWallet, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
